import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import RLoaderScreen from "../components/RLoaderScreen";

// How often to re-probe the backend while it's briefly unreachable (mid-deploy).
// ~6.5s ≈ one full trace-loader loop at the 0.3× speed used on the splash.
const BACKEND_RETRY_MS = 6500;

// Keep the branded splash up for at least this long after a successful login,
// even when auth resolves instantly — it doubles as an app-preload window
// before the redirect to /leads.
const MIN_LOGIN_SPLASH_MS = 5000;

const STRINGS = {
  en: {
    errors: {
      not_authorized: "Your email is not authorized. Contact the administrator to request access.",
      auth_failed: "Authentication failed. Please try again.",
      expired: "Your session has expired. Please sign in again.",
    },
    subtitle_dev: "Dev Hub",
    subtitle_prod: "Operating System",
    loading_label: "Loading",
    welcome: "Welcome back.",
    sso_hint: "Sign in with your Resolute Solutions account to continue.",
    sso_button: "Continue with Microsoft",
    dev_hint: "SSO not configured. Select a role to sign in as a test user.",
    logging_in: "Signing in...",
    login_as: "Sign in as",
    bypass_login: "Sign in as Admin",
    bypass_loading: "Signing in...",
    bypass_desc: "Full access — settings, users, ops, all data",
    footer: "Access restricted to @resolutesolns.com accounts",
    dev_roles: [
      { role: "admin",             label: "Admin",             desc: "Full access — settings, users, ops, all data" },
      { role: "manager",           label: "Manager",           desc: "Analyst access + team filtering and reassignment" },
      { role: "analyst",           label: "Analyst",           desc: "View all, disposition, reprocess, responses" },
      { role: "account_executive", label: "Account Executive", desc: "View all, edit own assignments only" },
    ],
  },
  es: {
    errors: {
      not_authorized: "Tu correo no está autorizado. Contacta al administrador para solicitar acceso.",
      auth_failed: "Autenticación fallida. Por favor intenta de nuevo.",
      expired: "Tu sesión ha expirado. Por favor inicia sesión de nuevo.",
    },
    subtitle_dev: "Dev Hub",
    subtitle_prod: "Operating System",
    loading_label: "Cargando",
    welcome: "Bienvenido.",
    sso_hint: "Inicia sesión con tu cuenta de Resolute Solutions para continuar.",
    sso_button: "Continuar con Microsoft",
    dev_hint: "SSO no configurado. Selecciona un rol para iniciar sesión como usuario de prueba.",
    logging_in: "Iniciando sesión...",
    login_as: "Ingresar como",
    bypass_login: "Ingresar como Admin",
    bypass_loading: "Iniciando sesión...",
    bypass_desc: "Acceso total — configuración, usuarios, ops, todos los datos",
    footer: "Acceso restringido a cuentas @resolutesolns.com",
    dev_roles: [
      { role: "admin",             label: "Admin",             desc: "Acceso total — configuración, usuarios, ops, todos los datos" },
      { role: "manager",           label: "Manager",           desc: "Acceso analista + filtrado de equipo y reasignación" },
      { role: "analyst",           label: "Analyst",           desc: "Ver todo, disposición, reprocesar, respuestas" },
      { role: "account_executive", label: "Account Executive", desc: "Ver todo, editar solo asignaciones propias" },
    ],
  },
};

const DEV_ROLE_ACCENT = {
  admin:             { border: "#D43333", bg: "rgba(212,51,51,0.08)",   text: "#D43333"  },
  manager:           { border: "#7C3AED", bg: "rgba(124,58,237,0.08)",  text: "#7C3AED"  },
  analyst:           { border: "#006EF5", bg: "rgba(0,110,245,0.08)",   text: "#006EF5"  },
  account_executive: { border: "#0D9E6A", bg: "rgba(13,158,106,0.08)",  text: "#0D9E6A"  },
};

const RS_BLUE = "#006EF5";
const RS_NAVY = "#07054B";

export default function Login() {
  const [params] = useSearchParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const error     = params.get("error");

  const lang     = navigator.language?.startsWith("es") ? "es" : "en";
  const t        = STRINGS[lang];
  const errorMsg = t.errors[error] || error;

  const [devMode,         setDevMode]         = useState(false);
  const [devLoading,      setDevLoading]      = useState(true);
  const [loggingIn,       setLoggingIn]       = useState(null);
  const [bypassAvailable, setBypassAvailable] = useState(false);
  const [bypassLoading,   setBypassLoading]   = useState(false);
  const [backendDown,     setBackendDown]     = useState(false);

  useEffect(() => {
    if (user) navigate("/leads", { replace: true });
  }, [user, navigate]);

  // Probe the backend for its auth mode. A 5xx (or a failed request) means the
  // server is briefly unreachable — almost always a deploy in progress — so we
  // raise backendDown and let the splash + retry loop ride it out. A clean
  // response clears backendDown so a recovered server returns the form.
  const runDevCheck = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/dev-check");
      if (res.status === 502 || res.status === 503 || res.status === 504) {
        setBackendDown(true);
        return;
      }
      const data = res.ok ? await res.json() : { dev_mode: false };
      setDevMode(data.dev_mode);
      setBackendDown(false);
    } catch {
      setBackendDown(true);
    } finally {
      setDevLoading(false);
    }
  }, []);

  useEffect(() => {
    runDevCheck();
  }, [runDevCheck]);

  // While the backend is unreachable, keep re-probing so the deploy window is
  // invisible: the loader animates and the form appears as soon as it recovers.
  useEffect(() => {
    if (!backendDown) return undefined;
    const id = setInterval(runDevCheck, BACKEND_RETRY_MS);
    return () => clearInterval(id);
  }, [backendDown, runDevCheck]);

  useEffect(() => {
    if (backendDown) return;
    fetch("/api/auth/bypass-check")
      .then((r) => r.ok ? r.json() : { bypass_available: false })
      .then((data) => setBypassAvailable(data.bypass_available))
      .catch(() => setBypassAvailable(false));
  }, [backendDown]);

  // Redirect to the app once the splash has been shown for at least
  // MIN_LOGIN_SPLASH_MS, so the loader is always visible long enough to cover
  // app preload. The active loading flag is left set so the splash stays up
  // through the wait — the hard navigation tears the page down afterwards.
  const redirectToAppAfterSplash = (startedAt) => {
    const remaining = Math.max(0, MIN_LOGIN_SPLASH_MS - (Date.now() - startedAt));
    setTimeout(() => {
      window.location.href = import.meta.env.BASE_URL + "leads";
    }, remaining);
  };

  const handleBypassLogin = async () => {
    const headers = {};
    if (!devMode) {
      const token = prompt(lang === "es" ? "Ingresa el token de acceso admin:" : "Enter admin access token:");
      if (!token) return;
      headers["X-Bypass-Token"] = token;
    }
    const startedAt = Date.now();
    setBypassLoading(true);
    try {
      const res = await fetch("/api/auth/bypass-login", { method: "POST", headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || (lang === "es" ? "Bypass login fallido" : "Bypass login failed"));
        setBypassLoading(false);
        return;
      }
      redirectToAppAfterSplash(startedAt);
    } catch {
      alert(lang === "es" ? "No se pudo conectar con el servidor." : "Could not connect to the server.");
      setBypassLoading(false);
    }
  };

  const handleDevLogin = async (role) => {
    const startedAt = Date.now();
    setLoggingIn(role);
    try {
      const res = await fetch("/api/auth/dev-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || (lang === "es" ? "Dev login fallido" : "Dev login failed"));
        setLoggingIn(null);
        return;
      }
      redirectToAppAfterSplash(startedAt);
    } catch {
      alert(lang === "es" ? "No se pudo conectar con el servidor." : "Could not connect to the server.");
      setLoggingIn(null);
    }
  };

  // Login is a branded marketing surface — always light form + navy brand panel.
  // App theme (dark mode) must not restyle this page or the left column collapses
  // into near-black and blends with the right panel.
  const panelBg = "#ffffff";
  const panelText = RS_NAVY;
  const panelSub = "#8B8BAD";
  const border = "#E6E6ED";

  const BASE = import.meta.env.BASE_URL;

  // Branded splash while: the backend answers the auth-mode probe, it's briefly
  // unreachable mid-deploy (the trace loader loops until recovery), or a sign-in
  // is in flight — covering the dev/admin login POST and the redirect to /leads
  // so the click lands on the branded loader instead of an unchanged button.
  const signingIn = loggingIn !== null || bypassLoading;
  if (devLoading || backendDown || signingIn) {
    return (
      <RLoaderScreen
        label={t.loading_label}
        variant="trace"
        bg="navy"
        speed={0.3}
        size={100}
      />
    );
  }

  return (
    <>
      <style>{`
        .rs-right { display: flex; }
        @media (max-width: 860px) {
          .rs-right { display: none !important; }
          .rs-left  { flex: 1 !important; max-width: 100% !important; width: 100% !important; }
        }
        .rs-sso-btn:hover  { background: #0058C4 !important; }
        .rs-sso-btn:active { background: #004293 !important; }
        .rs-navy-btn:hover  { background: #373569 !important; }
        .rs-navy-btn:active { background: #06043C !important; }
        .rs-role-btn:hover { opacity: 1 !important; }
      `}</style>

      <div style={{ display: "flex", minHeight: "100vh", backgroundColor: panelBg }}>

        {/* ── LEFT PANEL — Form ─────────────────────────────────────── */}
        <div
          className="rs-left"
          style={{
            flex: "0 0 auto",
            width: "min(500px, 42vw)",
            maxWidth: "560px",
            minWidth: "320px",
            display: "flex",
            flexDirection: "column",
            backgroundColor: panelBg,
            borderRight: `1px solid ${border}`,
          }}
        >
          {/* Logo */}
          <div style={{ padding: "40px 48px 0" }}>
            <img
              src={`${BASE}logo-full-color.png`}
              alt="Resolute"
              style={{ height: "28px", display: "block" }}
            />
          </div>

          {/* Auth content */}
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "0 48px",
          }}>

            <p style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: RS_BLUE,
              marginBottom: "14px",
            }}>
              {devMode ? "Development Mode" : "Secure Access"}
            </p>

            <h1 style={{
              fontSize: "36px",
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              color: panelText,
              marginBottom: "8px",
            }}>
              {t.welcome}
            </h1>

            <p style={{
              fontSize: "14px",
              fontWeight: 500,
              color: panelSub,
              letterSpacing: "0.01em",
              marginBottom: "36px",
            }}>
              Resolute Operations System
            </p>

            {/* Error */}
            {errorMsg && (
              <div style={{
                marginBottom: "24px",
                padding: "12px 16px",
                borderRadius: "4px",
                border: "1.5px solid #D43333",
                backgroundColor: "rgba(212,51,51,0.08)",
                color: "#D43333",
                fontSize: "14px",
                lineHeight: 1.55,
              }}>
                {errorMsg}
              </div>
            )}

            {/* SSO */}
            {!devMode && (
              <a
                href={`${BASE}api/auth/login`}
                className="rs-sso-btn"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "14px 24px",
                  backgroundColor: RS_BLUE,
                  color: "#ffffff",
                  fontFamily: "inherit",
                  fontSize: "16px",
                  fontWeight: 600,
                  lineHeight: 1,
                  borderRadius: "4px",
                  border: "1.5px solid transparent",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background 180ms cubic-bezier(.2,0,0,1)",
                  boxShadow: "0 4px 10px rgba(0,110,245,0.22), 0 1px 3px rgba(7,5,75,0.08)",
                  minHeight: "50px",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                {t.sso_button}
              </a>
            )}

            {/* Dev roles */}
            {devMode && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {t.dev_roles.map(({ role, label, desc }) => {
                  const ac       = DEV_ROLE_ACCENT[role];
                  const isActive = loggingIn === role;
                  return (
                    <button
                      key={role}
                      onClick={() => handleDevLogin(role)}
                      disabled={loggingIn !== null}
                      className="rs-role-btn"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "12px 16px 12px 14px",
                        borderRadius: "4px",
                        border: `1.5px solid ${isActive ? ac.border : border}`,
                        borderLeft: `3px solid ${ac.border}`,
                        backgroundColor: isActive ? ac.bg : "#FAFAFA",
                        cursor: loggingIn !== null ? "not-allowed" : "pointer",
                        opacity: loggingIn !== null && !isActive ? 0.4 : 1,
                        transition: "all 180ms cubic-bezier(.2,0,0,1)",
                        fontFamily: "inherit",
                        boxShadow: isActive ? `0 0 0 3px ${ac.border}22` : "none",
                      }}
                    >
                      <span style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: isActive ? ac.text : panelText,
                        marginBottom: "2px",
                      }}>
                        {isActive ? t.logging_in : `${t.login_as} ${label}`}
                      </span>
                      <span style={{ display: "block", fontSize: "12px", color: panelSub }}>
                        {desc}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Admin bypass */}
            {bypassAvailable && (
              <div style={{ marginTop: "28px" }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "14px",
                }}>
                  <div style={{ flex: 1, height: "1px", backgroundColor: border }} />
                  <span style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#92600A",
                    whiteSpace: "nowrap",
                  }}>
                    Admin Access
                  </span>
                  <div style={{ flex: 1, height: "1px", backgroundColor: border }} />
                </div>
                <button
                  onClick={handleBypassLogin}
                  disabled={bypassLoading}
                  className="rs-navy-btn"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    border: `1.5px solid ${RS_NAVY}`,
                    backgroundColor: RS_NAVY,
                    color: "#ffffff",
                    cursor: bypassLoading ? "not-allowed" : "pointer",
                    opacity: bypassLoading ? 0.7 : 1,
                    transition: "background 180ms cubic-bezier(.2,0,0,1)",
                    fontFamily: "inherit",
                    minHeight: "50px",
                  }}
                >
                  <span style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "2px" }}>
                    {bypassLoading ? t.bypass_loading : t.bypass_login}
                  </span>
                  <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.55)" }}>
                    {t.bypass_desc}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Bottom: Resolute + footer */}
          <div style={{
            padding: "24px 48px 36px",
            borderTop: `1px solid ${border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}>
            <span style={{
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: RS_NAVY,
              opacity: 0.18,
              flexShrink: 0,
            }}>
              Resolute
            </span>
            <span style={{
              fontSize: "11px",
              color: panelSub,
              textAlign: "right",
              lineHeight: 1.4,
            }}>
              {t.footer}
            </span>
          </div>
        </div>

        {/* ── RIGHT PANEL — Brand ────────────────────────────────────── */}
        <div
          className="rs-right"
          style={{
            flex: 1,
            backgroundColor: RS_NAVY,
            position: "relative",
            overflow: "hidden",
            flexDirection: "column",
          }}
        >
          {/* Blue accent bar */}
          <div style={{
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "3px",
            backgroundColor: RS_BLUE,
            zIndex: 2,
          }} />

          {/* Diagonal texture */}
          <div style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              rgba(0,110,245,0.04) 0px,  rgba(0,110,245,0.04) 1px,
              transparent              1px, transparent              52px
            )`,
          }} />

          {/* R-mark watermark — bottom-right */}
          <div style={{
            position: "absolute",
            right: "-80px",
            bottom: "-80px",
            width: "460px",
            height: "460px",
            opacity: 0.06,
            pointerEvents: "none",
          }}>
            <img
              src={`${BASE}logo-icon-white.png`}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>

          {/* Content */}
          <div style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "52px 60px",
          }}>
            <div style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}>
              <p style={{
                fontSize: "36px",
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
                color: "#ffffff",
                maxWidth: "420px",
              }}>
                Pragmatic deployment of automation and AI for mission-critical outcomes.
              </p>
            </div>

            {/* Bottom: logo + classification */}
            <div style={{
              borderTop: "1px solid rgba(255,255,255,0.08)",
              paddingTop: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <img
                src={`${BASE}logo-full-white.png`}
                alt="Resolute"
                style={{ height: "22px", opacity: 0.65 }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <div style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#0D9E6A",
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.28)",
                  letterSpacing: "0.04em",
                }}>
                  Internal Use Only
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
